import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Loader, Tag, Info, Clock, History, X, Calendar } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import AddTransactionModal from "../components/AddTransactionModal";
import { accountService } from "../api/accountService";
import { 
  getActiveShift, 
  getShifts, 
  getShiftSummary, 
  formatShiftNumber, 
  getShiftStatusInfo, 
  formatShiftDuration,
  getCashEntries
} from "../api/shiftService";
import { useNotification } from "../contexts/NotificationContext";

export default function FinanceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [accountData, setAccountData] = useState(null);
  const [mutations, setMutations] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Shift filtering states
  const [activeShift, setActiveShift] = useState(null);
  const [activeTab, setActiveTab] = useState("current_shift"); // "current_shift" or "all"
  const [shiftsHistory, setShiftsHistory] = useState([]);
  const [loadingShifts, setLoadingShifts] = useState(false);
  const [selectedShiftId, setSelectedShiftId] = useState(null);
  const [shiftSummary, setShiftSummary] = useState(null);
  const [shiftCashEntries, setShiftCashEntries] = useState([]);
  const [loadingShiftSummary, setLoadingShiftSummary] = useState(false);
  
  const isLaciKasir = accountData?.name?.toLowerCase() === "laci kasir";

  const getAccountTypeLabel = (type) => {
    if (type === "PAYMENT_ACCOUNT") return "Kas/Rekening Pembayaran";
    if (type === "OPERATIONAL_ACCOUNT") return "Kas/Rekening Operasional";
    return type || "-";
  };

  // Fetch account details and mutations
  useEffect(() => {
    console.log("🔄 FinanceDetail: Fetching data...", {
      id,
      currentPage,
      refreshTrigger,
    });
    const fetchAccountData = async () => {
      try {
        setLoading(true);
        const [accountResponse, mutationsResponse] = await Promise.all([
          accountService.get(id),
          accountService.getMutations(id, {
            page: currentPage,
            limit: itemsPerPage,
          }),
        ]);

        console.log("🔍 Account Response:", accountResponse);
        console.log("📊 Account Status:", accountResponse?.status);
        console.log("✅ FinanceDetail: Data fetched successfully");

        const accountPayload = accountResponse?.data || accountResponse;
        const mutationPayload = mutationsResponse?.data || [];
        setAccountData(accountPayload);
        setMutations(Array.isArray(mutationPayload) ? mutationPayload : []);

        // Fetch active shift if Laci Kasir
        if (accountPayload?.name?.toLowerCase() === "laci kasir") {
          try {
            const shiftResponse = await getActiveShift();
            setActiveShift(shiftResponse?.data || shiftResponse);
          } catch (e) {
            console.log("No active shift found");
            setActiveShift(null);
          }
        }
      } catch (err) {
        console.error("Error fetching account data:", err);
        showError("Gagal memuat data akun");
      } finally {
        setLoading(false);
      }
    };

    fetchAccountData();
  }, [id, currentPage, itemsPerPage, refreshTrigger]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const filteredMutations = useMemo(() => {
    if (!isLaciKasir || activeTab === "all") return mutations;
    
    if (!activeShift) return [];

    const shiftStart = new Date(activeShift.opened_at).getTime();
    return mutations.filter(m => new Date(m.created_at).getTime() >= shiftStart);
  }, [mutations, isLaciKasir, activeTab, activeShift]);

  // Load shifts history
  useEffect(() => {
    if (isLaciKasir && activeTab === "all" && shiftsHistory.length === 0) {
      setLoadingShifts(true);
      getShifts({ limit: 50 })
        .then((res) => {
          setShiftsHistory(res?.data?.data || res?.data || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingShifts(false));
    }
  }, [isLaciKasir, activeTab, shiftsHistory.length]);

  const handleViewShiftDetail = async (shift) => {
    setSelectedShiftId(shift.id);
    setLoadingShiftSummary(true);
    setShiftSummary(null);
    setShiftCashEntries([]);
    try {
      const res = await getShiftSummary(shift.id);
      const summaryData = res?.data || res;
      setShiftSummary({
        ...shift,
        ...summaryData,
      });

      // Ambil cash entries (mutasi)
      const resEntries = await getCashEntries(shift.id);
      setShiftCashEntries(resEntries?.data?.data || resEntries?.data || []);
    } catch (err) {
      showError("Gagal memuat detail shift");
    } finally {
      setLoadingShiftSummary(false);
    }
  };

  // Calculate totals from mutations
  const calculateTotals = () => {
    let totalIn = 0;
    let totalOut = 0;

    filteredMutations.forEach((mut) => {
      if (mut.category === "IN") {
        totalIn += mut.amount;
      } else {
        totalOut += mut.amount;
      }
    });

    return { totalIn, totalOut };
  };

  const { totalIn, totalOut } = accountData
    ? calculateTotals()
    : { totalIn: 0, totalOut: 0 };

  const [isAdmin] = useState(() => {
    try {
      const data = JSON.parse(localStorage.getItem("user_data") || "{}");
      return data?.is_admin === true;
    } catch {
      return false;
    }
  });

  const handleBack = () => {
    if (isAdmin) {
      navigate("/keuangan");
    } else {
      navigate("/");
    }
  };

  const handleAddTransaction = () => {
    setIsAddModalOpen(true);
  };

  const handleSubmitTransaction = async (formData) => {
    let amount = 0;
    try {
      let notes = formData.keterangan || formData.notes || "";

      amount = parseFloat(formData.nominal || formData.amount || 0);
      if (!amount || amount <= 0) {
        showError("Nominal harus lebih dari 0");
        return false;
      }

      const kindMap = {
        PENDAPATAN: "INCOME",
        NON_PENDAPATAN: "NON_INCOME",
        BIAYA: "EXPENSE",
        NON_BIAYA: "NON_EXPENSE",
      };

      const selectedKind = kindMap[formData.jenis] || "NON_EXPENSE";

      let uploadedAttachment = null;
      if (formData.lampiran) {
        const uploadResponse = await accountService.uploadProof(
          formData.lampiran,
        );
        const uploadData = uploadResponse?.data || uploadResponse || {};
        const uploadedUrl =
          uploadData.url || uploadData.file_url || uploadData.path || "";

        if (!uploadedUrl) {
          showError("Upload lampiran gagal, URL file tidak ditemukan");
          return false;
        }

        uploadedAttachment = {
          url: uploadedUrl,
          filename: formData.lampiran.name,
          mime_type: formData.lampiran.type,
          size: formData.lampiran.size,
        };
      }

      const attachments = uploadedAttachment ? [uploadedAttachment] : [];

      const basePayload = {
        amount,
        notes: notes,
        attachments,
        reference_type: "MANUAL",
        reference_id: "",
      };

      if (formData.category === "masuk") {
        const depositPayload = {
          amount,
          kind: selectedKind,
          notes,
          reference_type: "MANUAL",
          reference_id: "",
        };

        if (uploadedAttachment) {
          depositPayload.proof_url = uploadedAttachment.url;
          depositPayload.proof_name = uploadedAttachment.filename;
          depositPayload.proof_type = uploadedAttachment.mime_type;
          depositPayload.proof_size = uploadedAttachment.size;
        }

        await accountService.deposit(id, { ...depositPayload, attachments });
        showSuccess("Uang masuk berhasil ditambahkan");
      } else {
        const withdrawPayload = {
          amount,
          kind: selectedKind,
          notes,
          reference_type: "MANUAL",
          reference_id: "",
        };

        if (uploadedAttachment) {
          withdrawPayload.proof_url = uploadedAttachment.url;
          withdrawPayload.proof_name = uploadedAttachment.filename;
          withdrawPayload.proof_type = uploadedAttachment.mime_type;
          withdrawPayload.proof_size = uploadedAttachment.size;
        }

        const finalWithdrawPayload = { ...withdrawPayload };
        if (attachments.length > 0) {
          finalWithdrawPayload.attachments = attachments;
        }

        console.log("📤 Withdraw payload:", JSON.stringify(finalWithdrawPayload, null, 2));
        await accountService.withdraw(id, finalWithdrawPayload);
        showSuccess("Uang keluar berhasil ditambahkan");
      }
      console.log("✅ Transaction successful, triggering refresh...");
      setIsAddModalOpen(false);
      setRefreshTrigger((prev) => prev + 1); // Trigger refresh
      return true;
    } catch (err) {
      console.error("Error submitting transaction:", err);
      const errMsg = err.message || err.response?.data?.message || "Gagal menambahkan transaksi";
      if (errMsg === "invalid transaction") {
        const actualBalance = accountData?.total_balance || 0;
        showError(`Saldo Laci Kasir aktual (Rp ${actualBalance.toLocaleString("id-ID")}) tidak mencukupi untuk menarik Rp ${amount.toLocaleString("id-ID")}. (Catatan: Estimasi Saldo Shift belum tentu sama dengan Saldo Aktual Laci Kasir)`);
      } else {
        showError(errMsg);
      }
      return false;
    }
  };

  const handleEditMutation = () => {
    showError("Fitur edit mutasi belum tersedia");
  };

  const handleDeleteMutation = () => {
    showError("Fitur hapus mutasi belum tersedia");
  };

  const handleViewReceipt = (mutation) => {
    const firstAttachment = mutation?.attachments?.[0];
    const receiptUrl = firstAttachment?.url || mutation?.proof_url;

    if (!receiptUrl) {
      showError("Receipt tidak tersedia untuk mutasi ini");
      return;
    }

    window.open(receiptUrl, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!accountData) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <p className="text-slate-500">Data tidak ditemukan</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="keuangan"
      />

      <div
        className={`flex-1 overflow-auto transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header />

        <div className="p-8">
          {/* === Hero Header === */}
          <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl p-4 lg:p-5 mb-5 overflow-hidden shadow-lg shadow-teal-900/10">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-48 h-48 bg-emerald-300/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label="Kembali"
                  title="Kembali"
                  className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 ring-1 ring-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0">
                  <h1 className="text-lg lg:text-xl font-bold text-white tracking-tight leading-tight break-words">
                    {accountData.name}
                  </h1>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 lg:justify-end">
                <span className="inline-flex items-center gap-1 text-xs text-teal-50 bg-white/10 px-2.5 py-1 rounded-full ring-1 ring-white/20">
                  <Tag className="w-3 h-3" />{" "}
                  {getAccountTypeLabel(accountData.type)}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ring-1 ${
                    accountData.status === "ACTIVE"
                      ? "bg-emerald-400/20 text-emerald-50 ring-emerald-300/40"
                      : "bg-rose-400/20 text-rose-50 ring-rose-300/40"
                  }`}>
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${
                      accountData.status === "ACTIVE"
                        ? "bg-emerald-300"
                        : "bg-rose-300"
                    }`}
                  />
                  {accountData.status === "ACTIVE"
                    ? "Aktif"
                    : accountData.status === "INACTIVE"
                      ? "Non-Aktif"
                      : accountData.status}
                </span>
              </div>
            </div>
          </div>

          {/* Info Cards */}
          {!(isLaciKasir && activeTab === "all") && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <div className="bg-white ring-1 ring-slate-200/70 rounded-xl p-4 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-1">
                  Total Uang Masuk
                </p>
                <p className="text-lg font-bold text-emerald-700">
                  Rp {totalIn.toLocaleString("id-ID")}
                </p>
              </div>
              <div className="bg-white ring-1 ring-slate-200/70 rounded-xl p-4 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-1">
                  Total Uang Keluar
                </p>
                <p className="text-lg font-bold text-orange-600">
                  Rp {totalOut.toLocaleString("id-ID")}
                </p>
              </div>
              <div className="bg-teal-50/60 ring-1 ring-teal-200/70 rounded-xl p-4 shadow-sm">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700 mb-1">
                  {isLaciKasir && activeTab === "current_shift" ? "Saldo Laci Kasir (Aktual DB)" : "Total Saldo Akhir"}
                </p>
                <p className="text-lg font-bold text-teal-900">
                  Rp {(accountData.total_balance ?? 0).toLocaleString("id-ID")}
                </p>
                {isLaciKasir && activeTab === "current_shift" && (
                  <p className="text-[11px] text-teal-600/80 mt-0.5 font-medium">
                    Estimasi Shift: Rp {(totalIn - totalOut).toLocaleString("id-ID")}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Tabs and Table */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 overflow-hidden shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between px-6 py-4 border-b border-slate-100 gap-4">
              {isLaciKasir ? (
                <div className="flex bg-slate-100 p-1 rounded-xl w-fit">
                  <button
                    onClick={() => setActiveTab("current_shift")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === "current_shift"
                        ? "bg-white text-teal-700 shadow-sm ring-1 ring-slate-200/50"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                    }`}>
                    <Clock className="w-4 h-4" />
                    Shift Berjalan
                  </button>
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      activeTab === "all"
                        ? "bg-white text-teal-700 shadow-sm ring-1 ring-slate-200/50"
                        : "text-slate-500 hover:text-slate-700 hover:bg-slate-200/50"
                    }`}>
                    <History className="w-4 h-4" />
                    Semua Riwayat
                  </button>
                </div>
              ) : (
                <div className="text-lg font-bold text-slate-800">Riwayat Mutasi</div>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={handleAddTransaction}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium transition-colors hover:bg-teal-700 shadow-sm shadow-teal-600/20">
                  <Plus className="w-4 h-4" />
                  Tambah Mutasi
                </button>
              </div>
            </div>

            {/* Table Area */}
            <div className="overflow-x-auto">
              {isLaciKasir && activeTab === "all" ? (
                // SHIFT HISTORY TABLE
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Aksi</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Nomor Shift</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Waktu Buka</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Waktu Tutup</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Kasir</th>
                      <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loadingShifts ? (
                      <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500"><Loader className="w-6 h-6 animate-spin mx-auto text-teal-600"/></td></tr>
                    ) : shiftsHistory.length === 0 ? (
                      <tr><td colSpan="6" className="px-6 py-12 text-center text-slate-500">Belum ada riwayat shift</td></tr>
                    ) : (
                      shiftsHistory.map((shift) => (
                        <tr key={shift.id} className="hover:bg-slate-50/70 transition-colors">
                          <td className="px-6 py-4">
                            <button
                              onClick={() => handleViewShiftDetail(shift)}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition shadow-sm shadow-teal-600/20"
                              title="Lihat Detail Shift">
                              <Info className="w-4 h-4" />
                            </button>
                          </td>
                          <td className="px-6 py-4 font-medium text-slate-800">{formatShiftNumber(shift.shift_number)}</td>
                          <td className="px-6 py-4 text-slate-600 text-sm">{formatDate(shift.opened_at)}</td>
                          <td className="px-6 py-4 text-slate-600 text-sm">{shift.closed_at ? formatDate(shift.closed_at) : "-"}</td>
                          <td className="px-6 py-4 text-slate-600 text-sm">{shift.opened_by_name || "Kasir"}</td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${getShiftStatusInfo(shift.status).color}`}>
                              {getShiftStatusInfo(shift.status).label}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : (
                // MUTATIONS TABLE
                <table className="w-full">
                  <thead className="bg-slate-50/80 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Aksi
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Kategori
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Waktu
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Jenis
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Nominal
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Keterangan
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredMutations.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-12 text-center text-slate-500">
                        {isLaciKasir && activeTab === "current_shift" && !activeShift ? (
                          <div className="flex flex-col items-center gap-2">
                            <Clock className="w-8 h-8 text-slate-300" />
                            <p>Tidak ada Shift Kasir yang sedang aktif</p>
                          </div>
                        ) : (
                          "Belum ada transaksi"
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredMutations.map((mutation) => (
                      <tr
                        key={mutation.id}
                        className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={handleEditMutation}
                              className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition shadow-sm shadow-teal-600/20"
                              title="Edit">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={handleDeleteMutation}
                              className="w-8 h-8 bg-rose-600 text-white rounded-lg hover:bg-rose-700 flex items-center justify-center transition shadow-sm shadow-rose-600/20"
                              title="Hapus">
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleViewReceipt(mutation)}
                              className="w-8 h-8 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center justify-center transition shadow-sm shadow-emerald-600/20"
                              title="Detail / Lihat Receipt">
                              <Info className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${
                              mutation.category === "IN"
                                ? "bg-teal-50 text-teal-700 ring-teal-200"
                                : "bg-orange-50 text-orange-700 ring-orange-200"
                            }`}>
                            {mutation.category === "IN"
                              ? "Uang Masuk"
                              : "Uang Keluar"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {formatDate(mutation.created_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {mutation.category === "IN"
                            ? mutation.kind === "INCOME" || mutation.reference_type === "SALES"
                              ? "Pendapatan"
                              : mutation.kind === "NON_INCOME"
                                ? "Non-Pendapatan"
                                : mutation.reference_type || "-"
                            : mutation.reference_type === "SALES_REFUND"
                              ? "Pengembalian Dana / Void"
                              : mutation.kind === "EXPENSE"
                                ? "Biaya"
                                : mutation.kind === "NON_EXPENSE"
                                  ? "Non-Biaya"
                                  : mutation.reference_type || "-"}
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">
                          Rp {mutation.amount?.toLocaleString("id-ID")}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {mutation.notes || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      <AddTransactionModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleSubmitTransaction}
      />

      {/* Shift Detail Modal */}
      {selectedShiftId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b border-slate-100 bg-slate-50/50">
              <h3 className="text-lg font-bold text-slate-800">Detail Rekap Shift</h3>
              <button
                onClick={() => setSelectedShiftId(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto">
              {loadingShiftSummary ? (
                <div className="flex justify-center py-12"><Loader className="w-8 h-8 animate-spin text-teal-600"/></div>
              ) : shiftSummary ? (
                <div className="space-y-6">
                  {/* Summary Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-xl p-4 ring-1 ring-slate-100">
                      <p className="text-xs font-semibold text-slate-500 mb-1">Modal Awal</p>
                      <p className="text-lg font-bold text-slate-800">Rp {(shiftSummary.opening_cash || 0).toLocaleString("id-ID")}</p>
                    </div>
                    <div className="bg-emerald-50 rounded-xl p-4 ring-1 ring-emerald-100">
                      <p className="text-xs font-semibold text-emerald-600 mb-1">Uang Tunai Masuk</p>
                      <p className="text-lg font-bold text-emerald-700">Rp {((shiftSummary.cash_in || 0) - (shiftSummary.opening_cash || 0)).toLocaleString("id-ID")}</p>
                    </div>
                    <div className="bg-rose-50 rounded-xl p-4 ring-1 ring-rose-100">
                      <p className="text-xs font-semibold text-rose-600 mb-1">Total Kas Keluar</p>
                      <p className="text-lg font-bold text-rose-700">Rp {(shiftSummary.total_cash_out || 0).toLocaleString("id-ID")}</p>
                    </div>
                    <div className="bg-teal-50 rounded-xl p-4 ring-1 ring-teal-100">
                      <p className="text-xs font-semibold text-teal-700 mb-1">Estimasi Kas Akhir</p>
                      <p className="text-lg font-bold text-teal-900">Rp {(shiftSummary.total_cashier_cash || 0).toLocaleString("id-ID")}</p>
                    </div>
                  </div>
                  {shiftSummary.status === "CLOSED" && (
                    <div className="bg-white rounded-xl p-4 ring-1 ring-slate-200">
                      <div className="flex justify-between items-center mb-2">
                        <p className="text-sm font-semibold text-slate-700">Kas Aktual (Dihitung Fisik)</p>
                        <p className="text-lg font-bold text-slate-800">Rp {(shiftSummary.actual_closing_cash || 0).toLocaleString("id-ID")}</p>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-slate-100">
                        <p className="text-sm text-slate-500">Selisih</p>
                        <p className={`font-bold ${shiftSummary.cash_difference < 0 ? "text-rose-600" : shiftSummary.cash_difference > 0 ? "text-emerald-600" : "text-slate-600"}`}>
                          Rp {(shiftSummary.cash_difference || 0).toLocaleString("id-ID")}
                        </p>
                      </div>
                    </div>
                  )}

                  {shiftCashEntries.length > 0 && (
                    <div className="mt-8 border-t border-slate-100 pt-6">
                      <h4 className="text-sm font-bold text-slate-800 mb-4">Riwayat Mutasi</h4>
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden overflow-x-auto">
                        <table className="w-full text-left whitespace-nowrap">
                          <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-semibold border-b border-slate-200">
                            <tr>
                              <th className="px-4 py-3">Kategori</th>
                              <th className="px-4 py-3">Waktu</th>
                              <th className="px-4 py-3">Nominal</th>
                              <th className="px-4 py-3">Keterangan</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {shiftCashEntries.map((entry) => (
                              <tr key={entry.id} className="hover:bg-slate-50/50">
                                <td className="px-4 py-3">
                                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-[11px] font-semibold ring-1 ${
                                    entry.type === "CASH_IN" 
                                      ? "bg-teal-50 text-teal-700 ring-teal-200" 
                                      : "bg-orange-50 text-orange-700 ring-orange-200"
                                  }`}>
                                    {entry.type === "CASH_IN" ? "Uang Masuk" : "Uang Keluar"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-700">
                                  {formatDate(entry.created_at)}
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-slate-900">
                                  Rp {entry.amount?.toLocaleString("id-ID")}
                                </td>
                                <td className="px-4 py-3 text-sm text-slate-600">
                                  {entry.description || "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-slate-500 py-8">Data tidak ditemukan</div>
              )}
            </div>
            
            <div className="p-5 border-t border-slate-100 bg-slate-50/50 flex justify-end">
              <button
                onClick={() => setSelectedShiftId(null)}
                className="px-4 py-2 rounded-xl text-sm font-medium bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 transition-colors">
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
